import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate, useAuth } from '../../../global/contexts/auth/auth';
import { IconCheckCircle, IconEdit, IconGear, IconLock, IconPlus } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useUsersList, useDeactivateUser, useSetUserStatus } from '../contexts/users';
import { ROLE_LABEL } from '../utils/roles';
import UserForm from '../forms/user-form';
import RoleModal from '../forms/role-modal';

import '../../../common/styles/catalog.css';
import '../styles/users.css';

// Gestión de usuarios staff (admin-users.md). Lista (users:read) + alta/edición
// (users:write) + cambio de rol y activar/desactivar (users:admin). El endpoint solo
// pagina (limit/offset); la búsqueda por nombre/correo se filtra en el cliente.
const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

export default function UsersOverview() {
    const { user: me } = useAuth();
    const [search, setSearch] = useState('');
    const [form, setForm] = useState(null);   // null | 'new' | user(edit)
    const [roleFor, setRoleFor] = useState(null); // user cuyo rol se cambia

    const { data, isLoading, isFetching, error } = useUsersList({ limit: 100 });
    const { mutateAsync: deactivate, isPending: p1 } = useDeactivateUser();
    const { mutateAsync: reactivate, isPending: p2 } = useSetUserStatus();
    const busy = p1 || p2;
    const confirm = useConfirm();

    const items = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? items.length;
    const q = search.trim().toLowerCase();
    const visible = useMemo(() => (
        q ? items.filter((u) => [u.name, u.email, u.username].some((f) => String(f || '').toLowerCase().includes(q))) : items
    ), [items, q]);

    const toggleActive = async (u) => {
        if (u.id === me?.id) { toast.error('No podés desactivar tu propia cuenta'); return; }
        try {
            if (u.status === 2) {
                const ok = await confirm({
                    title: 'Desactivar usuario',
                    message: `¿Desactivar a "${u.name || u.email}"? Podés reactivarlo después.`,
                    confirmLabel: 'Desactivar',
                    danger: false,
                });
                if (!ok) return;
                await deactivate(u.id); toast.success('Usuario desactivado');
            } else {
                await reactivate({ id: u.id, status: 2 }); toast.success('Usuario reactivado');
            }
        } catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo cambiar el estado'); }
    };

    return (
        <div className="cat-overview">
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Usuarios</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="users" action="write">
                    <button type="button" className="cat-overview__add" onClick={() => setForm('new')}>
                        <IconPlus /> <span>Nuevo usuario</span>
                    </button>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o correo…" aria-label="Buscar usuarios por nombre o correo" />
                </div>
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver los usuarios.' : (error.message || 'No se pudieron cargar los usuarios')}</div>}
            {total > items.length && <p className="cat-overview__note">Mostrando los primeros {items.length} de {total} usuarios. Refiná la búsqueda.</p>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando usuarios…</div>
            ) : !visible.length ? (
                <div className="cat-overview__state"><p className="cat-overview__state-text">{q ? 'Ningún usuario coincide.' : 'Aún no hay usuarios.'}</p></div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table cat-table--compact">
                        <thead>
                            <tr><th>Nombre</th><th>Correo</th><th>Rol</th><th>Últ. ingreso</th><th>Estado</th><th className="cat-table__actions-col" aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {visible.map((u) => (
                                <tr key={u.id} className="cat-table__row">
                                    <td>
                                        <Gate domain="users" action="read" fallback={<span className="cat-table__name">{u.name || '—'}</span>}>
                                            <Link to={`/admin/users/${u.id}`} className="cat-table__name">{u.name || '—'}</Link>
                                        </Gate>
                                        {u.id === me?.id && <span className="usr-you">vos</span>}
                                    </td>
                                    <td>{u.email || <span className="cat-table__empty">—</span>}{u.username && <span className="usr-username"> · {u.username}</span>}</td>
                                    <td><span className={`usr-role usr-role--${u.role}`}>{ROLE_LABEL[u.role] || u.role}</span></td>
                                    <td>{u.last_login_at ? formatDateOnly(u.last_login_at) : <span className="cat-table__empty">nunca</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${u.status}`}>{STATUS_LABEL[u.status] || '—'}</span></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="users" action="write">
                                            <button type="button" className="cat-table__edit" onClick={() => setForm(u)} aria-label={`Editar ${u.name || u.email}`} title="Editar"><IconEdit /></button>
                                        </Gate>
                                        <Gate domain="users" action="admin">
                                            <button type="button" className="cat-table__edit" onClick={() => setRoleFor(u)} aria-label={`Cambiar rol de ${u.name || u.email}`} title="Cambiar rol"><IconGear /></button>
                                            <button type="button" className="cat-table__edit" onClick={() => toggleActive(u)} disabled={busy || u.id === me?.id} aria-label={u.status === 2 ? 'Desactivar' : 'Reactivar'} title={u.status === 2 ? 'Desactivar' : 'Reactivar'}>
                                                {u.status === 2 ? <IconLock /> : <IconCheckCircle />}
                                            </button>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {form && <UserForm user={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />}
            {roleFor && <RoleModal user={roleFor} onClose={() => setRoleFor(null)} onSaved={() => setRoleFor(null)} />}
        </div>
    );
}
